import type { FC } from "react";

import { TierBadge } from "@ui/components/TierBadge";
import { Typography } from "@ui/components/Typography";
import type { VideoPlayerContainerProps } from "@ui/components/VideoContainer/VideoContainer";
import { useArticleInfoDisplay } from "@ui/hooks/useArticleInfoDisplay/useArticleInfoDisplay";
import { useTranslations } from "@ui/providers/useTranslations";
import { getBlogArticleTypeIcon } from "@ui/utils/blog/getBlogArticleTypeIcon";
import classNames from "classnames";

import { PlayIcon } from "@jti/brand-icons/play";
import { UiCheckIcon } from "@jti/brand-icons/ui-check";
import { LinkBehaviour } from "@jti/core-components/components/LinkBehaviour";
import { PictureBehaviour } from "@jti/core-components/components/PictureBehaviour";
import type { BlogItemImage } from "@jti/fe-common";
import type { LoyaltyEarningType } from "@jti/rest-api-types";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";

import type { ArticleRibbonColor } from "../types";

import style from "./BlogItem.module.scss";

export type { BlogItemImage };

export type BlogItemVideo = VideoPlayerContainerProps;

export type BlogItemProps = {
  title: string;
  description?: string;
  subtitle: string;
  image: BlogItemImage | null;
  readTime?: string;
  creationDate?: string;
  path?: string;
  isFeatured: boolean;
  tier?: string;
  isAvailable?: boolean | null;
  points?: number | null;
  isRead?: boolean | null;
  isWatched?: boolean | null;
  isCompleted?: boolean | null;
  reversed?: boolean;
  showAuthor?: boolean;
  author?: string;
  isLink?: boolean;
  articleType?: LoyaltyEarningType;
  controlColor?: ArticleRibbonColor;
  isLogged?: boolean;
};

export const BlogItem: FC<BlogItemProps> = ({
  title,
  description,
  subtitle,
  image,
  readTime,
  creationDate,
  path,
  points,
  tier,
  isAvailable,
  isFeatured = false,
  isRead,
  isWatched,
  isCompleted,
  reversed = false,
  showAuthor = false,
  author,
  isLink = true,
  articleType = "article",
  controlColor = "dark",
  isLogged = false,
}) => {
  const { __ } = useTranslations();
  const articleInfo = useArticleInfoDisplay(creationDate, readTime);

  const isAuthorVisible = !!(showAuthor && author);
  const isBottomSectionVisible = isAuthorVisible || readTime || creationDate;
  const isTypeOfVideo = articleType === "video";
  const isTypeOfQuiz = articleType === "quiz";
  const showPoints = (points ?? 0) > 0 && isLogged;
  const isEarned = (isTypeOfVideo && isWatched) || (isTypeOfQuiz && isCompleted) || isRead;

  const { smallReference, mediumReference, largeReference, altText } = image ?? {};

  const classes = classNames(style.articleListItem, {
    [style.featured]: isFeatured,
    [style.reversed]: reversed,
    [style.nonLink]: !isLink,
  });

  const Element = isLink ? LinkBehaviour : "div";
  const BlogArticleTypeIcon = getBlogArticleTypeIcon({ articleType });

  return (
    <Element
      className={classes}
      data-testid="articlesListItem"
      {...(isLink && {
        href: path,
        onClick: () =>
          dispatchTrackingEvent(TrackingEvents.TrackingBlogArticleClick, {
            article_name: title,
          }),
      })}
    >
      <div className={style.contentWrapper}>
        {image && (
          <div className={style.pictureWrapper}>
            <PictureBehaviour
              sources={[
                { srcSet: smallReference, media: "(max-width: 768px)" },
                { srcSet: mediumReference, media: "(max-width: 1919px)" },
              ]}
              imageProps={{
                className: style.image,
                src: largeReference,
                alt: altText,
                "data-testid": "articlesListItemImage",
              }}
            />
          </div>
        )}

        {isTypeOfVideo && (
          <div className={style.videoWrapper} data-testid="videoWrapper">
            <PlayIcon height={50} width={50} />
          </div>
        )}

        <TierBadge className={style.badge} tier={tier} isAvailable={isAvailable ?? undefined} />

        {showPoints && (
          <p
            data-testid="articleListItemPoints"
            className={classNames(style.points, controlColor === "light" ? "dark-theme" : "light-theme")}
          >
            {isEarned && <UiCheckIcon height={20} width={20} data-testid="articleReadIcon" />}

            {!isEarned && articleType && BlogArticleTypeIcon && (
              <BlogArticleTypeIcon height={20} width={20} data-testid="articleTypeIcon" />
            )}
            {__(
              `loyalty.articleRestrictedContentHeader.${isEarned ? "pointsEarned" : "earnPoints"}`,
              points != null ? [points] : undefined
            )}
          </p>
        )}
      </div>

      <div className={style.textSection}>
        <div className={style.content}>
          <span className={style.subtitle}>{subtitle}</span>
          <div className={style.innerContent}>
            <Typography variant="h3" className={style.title}>
              {title}
            </Typography>
            {isFeatured && <div className={style.description}>{description}</div>}
          </div>
        </div>
        {isBottomSectionVisible && (
          <div className={style.bottomSection}>
            {isAuthorVisible && <div data-testid="author">{author}</div>}
            {articleInfo}
          </div>
        )}
      </div>
    </Element>
  );
};
